import React, { Component } from 'react';
import ReactTable from 'react-table';
import XLSX from 'xlsx';
import { sessionsTableSkeletonScreen } from '../../../SkeletonScreens/SkeletonScreen';
import './WebinarsTable.css';
import { connect } from 'react-redux';
import { denormalizeWebinars } from '../../../Reducks/Webinars/WebinarsUtils';
import { CHECKBOX, VALUE_ADD } from '../../../Telemetry/Mixpanel/Constants/mixpanelConstants';
import {
  trackAnalyticsFeature,
  trackAnalyticsGenerateReportFeature
} from '../../../Telemetry/Mixpanel/Features/analyticsTracker';
import { XLS } from '../../../Utils/Constants';
import { HeaderSortComponent } from '../../../Scss/Custom/ReactTable/HeaderSortComponent';
import { actions } from '../../../Reducks/Webinars/index';
import {
  dateFormatterWithTimezone,
  dateSorter,
  getWebinarsData,
  startTimeFormatter,
  startTimeSorter
} from '../../../Utils/TableUtil';
import PropTypes from 'prop-types';
import SimpleSearchBox from '../../../Components/SearchBox/SimpleSearchBox';
import Menu from '../../../Components/Menu/Menu';
import DownloadIcon from '../../../Components/Buttons/DownloadIcon';
import { getReportTypeMenuItems } from '../../../Components/Menu/MenuItemsHelper';
import { getUnion } from '../../../Utils/ArrayUtils';

export class WebinarsTable extends Component {
  static contextTypes = {
    t: PropTypes.func.isRequired
  };
  static propTypes = {
    webinars: PropTypes.object,
    toggleWebinar: PropTypes.func,
    filterWebinars: PropTypes.func,
    toggleAllWebinars: PropTypes.func,
    mixpanelData: PropTypes.shape({
      action: PropTypes.string.isRequired,
      reportType: PropTypes.string
    })
  };

  getHeaders = () => {
    const { t } = this.context;
    const columns = this.getWebinarColumns();
    const header = [];
    columns.forEach(({ show, accessor }) => {
      if (accessor) {
        switch (accessor) {
          case WebinarsTableAccessor.NAME:
            header.push([t('tableHeaderColumns.name'), accessor]);
            break;
          case WebinarsTableAccessor.DATE:
            header.push([t('tableHeaderColumns.date'), accessor]);
            break;
          case WebinarsTableAccessor.START_TIME:
            header.push([t('tableHeaderColumns.startTime'), accessor]);
            break;
          case WebinarsTableAccessor.REGISTRANTS:
            header.push([t('tableHeaderColumns.registrants'), accessor]);
            break;
          default:
            console.error('unexpected case');
            break;
        }
      }
    });
    return header;
  };

  getRecords = (webinarsData, headers) => {
    const data = [];
    const { filteredKeys } = this.props.webinars.webinars;
    webinarsData.forEach(webinar => {
      let row = [];
      if (filteredKeys.includes(webinar.webinarKey)) {
        headers.forEach(element => {
          switch (element) {
            case WebinarsTableAccessor.DATE:
              row.push(dateFormatterWithTimezone(webinar[element].dateTime, webinar[element].timeZone));
              break;
            case WebinarsTableAccessor.START_TIME:
              row.push(startTimeFormatter({ value: webinar[element] }));
              break;
            default:
              row.push(webinar[element]);
          }
        });
        data.push(row);
      }
    });
    return data;
  };

  exportFile = (webinarsData, id) => {
    const { t } = this.context;
    const fileName = t('webinars.registrationsReportName');
    const headers = this.getHeaders();
    const records = this.getRecords(webinarsData, headers.map(item => item[1]));
    const data = getUnion([headers.map(item => item[0])], records);
    const workBook = XLSX.utils.book_new();
    const dataSheet = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(workBook, dataSheet);
    XLSX.writeFile(workBook, `${fileName}.${id === XLS ? 'xlsx' : 'csv'}`);
  };

  render() {
    const { fetching, webinars, filtering } = this.props.webinars;
    const filteredWebinars = denormalizeWebinars(webinars.filteredKeys, webinars.byKey);
    return (
      <div className="webinarsTableSection">
        {this.getSearchBox(filteredWebinars)}
        {fetching || filtering ? (
          sessionsTableSkeletonScreen()
        ) : (
          <React.Fragment>{this.getWebinarsTable(filteredWebinars)}</React.Fragment>
        )}
      </div>
    );
  }

  getWebinarsTable = filteredWebinars => {
    return (
      <ReactTable
        keyField="webinarKey"
        data={getWebinarsData(filteredWebinars)}
        columns={this.getWebinarColumns()}
        defaultPageSize={filteredWebinars.length}
        minRows={5}
        style={{ height: '270px' }}
        showPagination={false}
        className="-highlight"
        defaultSorted={[{ id: 'date', desc: true }]}
      />
    );
  };

  getSearchBox = filteredWebinars => {
    const { mixpanelData, uniqueId } = this.props;
    const { t } = this.context;
    const { filter } = this.props.webinars;
    const { isBig } = this.props;
    const detailsDownloadMenu = [];
    const menuDownloadItems = getReportTypeMenuItems();
    const webinarsData = getWebinarsData(filteredWebinars);
    menuDownloadItems.map(item =>
      detailsDownloadMenu.push({
        onClick: () => this.exportFile(webinarsData, item.id),
        text: item.title,
        iconClassName: item.iconClass,
        id: item.id
      })
    );
    return (
      <div className="searchBoxContainer">
        <SimpleSearchBox
          searchString={filter.searchString}
          placeHolder={t('webinars.search')}
          onSearchInputChange={value => this.props.filterWebinars({ searchString: value })}
          mixpanelData={mixpanelData}
        />
        <Menu
          isBig={isBig}
          menuKey={`download_${uniqueId}`}
          buttonName={t('buttons.downloadCamelCase')}
          detailsMenu={detailsDownloadMenu}
          buttonComponent={<DownloadIcon isBig={isBig} />}
        />
      </div>
    );
  };

  getWebinarColumns = () => {
    const { t } = this.context;
    const { filteredSelectedWebinarKeys, filteredKeys } = this.props.webinars.webinars;
    return [
      {
        accessor: '',
        Header: this.checkBoxHeaderFormatter(filteredKeys.length, filteredSelectedWebinarKeys.length),
        Cell: this.checkBoxCellFormatter,
        sortable: false,
        width: 35,
        resizable: false,
        style: { textAlign: 'center' },
        headerStyle: { textAlign: 'center' }
      },
      {
        accessor: WebinarsTableAccessor.NAME,
        Header: <HeaderSortComponent headerTitle={t('tableHeaderColumns.name')} />,
        minWidth: 200
      },
      {
        accessor: WebinarsTableAccessor.DATE,
        Header: <HeaderSortComponent headerTitle={t('tableHeaderColumns.date')} />,
        Cell: cell => dateFormatterWithTimezone(cell.value.dateTime, cell.value.timeZone),
        sortMethod: dateSorter
      },
      {
        accessor: WebinarsTableAccessor.START_TIME,
        Header: <HeaderSortComponent headerTitle={t('tableHeaderColumns.startTime')} />,
        Cell: startTimeFormatter,
        sortMethod: startTimeSorter
      },
      {
        accessor: WebinarsTableAccessor.REGISTRANTS,
        Header: <HeaderSortComponent headerTitle={t('tableHeaderColumns.registrants')} />
      }
    ];
  };

  checkBoxHeaderFormatter = (totalCount, selectedCount) => {
    const checked = selectedCount !== 0;
    const isIntermediate = selectedCount !== 0 && selectedCount !== totalCount;
    const onCheck = e => {
      trackAnalyticsFeature(VALUE_ADD, this.props.mixpanelData.action, CHECKBOX);
      if (this.props.mixpanelData.reportType) {
        trackAnalyticsGenerateReportFeature(this.props.mixpanelData.reportType, CHECKBOX);
      }
      this.props.toggleAllWebinars(e.target.checked);
    };
    const intermediate = input => {
      if (input) {
        input.indeterminate = isIntermediate;
      }
    };
    return <input type="checkbox" ref={intermediate} checked={checked} onChange={onCheck} />;
  };

  checkBoxCellFormatter = ({ original }) => {
    return (
      <input
        type="checkbox"
        checked={this.props.webinars.webinars.filteredSelectedWebinarKeys.includes(original.webinarKey)}
        onChange={e => {
          trackAnalyticsFeature(VALUE_ADD, this.props.mixpanelData.action, CHECKBOX);
          if (this.props.mixpanelData.reportType) {
            trackAnalyticsGenerateReportFeature(this.props.mixpanelData.reportType, CHECKBOX);
          }
          this.props.toggleWebinar(original.webinarKey, e.target.checked);
        }}
      />
    );
  };
}

export default connect(
  state => ({ webinars: state.reduced_webinars }),
  {
    toggleWebinar: actions.toggleWebinar,
    filterWebinars: actions.filterWebinars,
    toggleAllWebinars: actions.toggleAllWebinars
  }
)(WebinarsTable);

class WebinarsTableAccessor {
  static get NAME() {
    return 'name';
  }
  static get DATE() {
    return 'date';
  }
  static get START_TIME() {
    return 'startTime';
  }
  static get REGISTRANTS() {
    return 'registrants';
  }
}



// WEBPACK FOOTER //
// ./src/GoToWebinar/Webinars/Table/WebinarsTable.js